import { ref } from "vue";
import axios, { AxiosRequestConfig, AxiosError } from "axios";

// Default cache TTL: 15 minutes in milliseconds
const DEFAULT_CACHE_TTL = 15 * 60 * 1000;

// Simple hash function (FNV-1a)
function hashKey(str: string): string {
    let hash = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        hash = (hash * 0x01000193) >>> 0;
    }
    return "x-" + hash.toString(16);
}

export function useApiProxy<T>() {
    const data = ref<T | null>(null);
    const error = ref<string | null>(null);
    const loading = ref<boolean>(false);

    const generateKey = (name: string) => hashKey(name);

    const request = async (
        url: string,
        options?: AxiosRequestConfig,
        cache: boolean = false,
        ttl?: number,
        cacheSalt?: string
    ): Promise<T> => {
        loading.value = true;
        error.value = null;

        const key = generateKey(url + (cacheSalt || ""));

        const cacheTtl = ttl ?? DEFAULT_CACHE_TTL;

        // Check for cached data
        if (cache) {
            const cached = localStorage.getItem(key);
            if (cached) {
                try {
                    const cachedData = JSON.parse(cached);

                    // Check TTL expiration
                    if (cachedData.expiresAt && Date.now() > cachedData.expiresAt) {
                        console.debug(`[proxy] Cache expired for ${url} → key=${key}`);
                        localStorage.removeItem(key);
                    } else {
                        data.value = cachedData.data || cachedData;
                        console.debug(`[proxy] Cache hit for ${url} → key=${key}`);
                        loading.value = false;
                        return data.value;
                    }
                } catch {
                    console.warn(`[proxy] Cache parse error for ${url} → key=${key}`);
                    localStorage.removeItem(key);
                }
            } else {
                console.debug(`[proxy] Cache miss for ${url}`);
            }
        }

        try {
            const response = await axios(url, options);
            data.value = response.data;

            if (cache) {
                const cacheData = { data: data.value, expiresAt: Date.now() + cacheTtl };
                localStorage.setItem(key, JSON.stringify(cacheData));
                console.debug(`[proxy] Cached response for ${url} → key=${key} (TTL: ${cacheTtl}ms)`);
            }
        } catch (err) {
            const axiosError = err as AxiosError;
            error.value = axiosError.message || "An error occurred";
            console.error(`[proxy] Request failed for ${url}`, axiosError);
        } finally {
            loading.value = false;
        }

        return data.value;
    };

    return { data, error, loading, request };
}
